/**
 * Offer mode on a listing (RESPONDER_BADGE_WANTLIST_CLOSET_PLAN.md §5, 2026-10-20).
 *
 *   'open'  : trades and purchase offers (the default; a missing value means open,
 *             so every listing created before the field existed is open)
 *   'trade' : trade offers only, cash on top still allowed
 *   'sell'  : purchase offers only; never selectable as a pedal to give in a trade
 *
 * Everything that reads the mode goes through these helpers so the "missing
 * means open" rule lives in one place. The server copy is
 * `normalizeOfferMode` / `isSaleOnly` in functions/matching.js (used by the
 * matcher, the match validator, interest cleanup and the emails) and the
 * offer-create and interest-create checks in firestore.rules.
 */
export const OFFER_MODES = ['open', 'trade', 'sell'];

export function normalizeOfferMode(value) {
  return value === 'trade' || value === 'sell' ? value : 'open';
}

export function offerModeOf(listing) {
  return normalizeOfferMode(listing && listing.offerMode);
}

export function allowsTrade(listing) {
  return offerModeOf(listing) !== 'sell';
}

export function allowsPurchase(listing) {
  return offerModeOf(listing) !== 'trade';
}

// Chip wording: two words each (Jason, 2026-10-20). Open listings show a chip too.
export const OFFER_MODE_LABELS = {
  open: 'Any offer',
  trade: 'Trade only',
  sell: 'Sale only',
};

export const OFFER_MODE_DESCRIPTIONS = {
  open: 'Accept trade offers and offers to buy.',
  trade: 'Trade offers only. Cash on top of a trade is still fine.',
  sell: 'Offers to buy only. This pedal will not be offered in trades.',
};

export function offerModeLabel(listing) {
  return OFFER_MODE_LABELS[offerModeOf(listing)];
}

// Filter control on browse, search, matches and Saved Pedals (plan §5, Jason
// 2026-10-20): the same three choices as the listing form, by the listing's
// own setting, one at a time (picking one clears the others). Nothing
// selected means every listing.
export const OFFER_FILTER_OPTIONS = [OFFER_MODE_LABELS.open, OFFER_MODE_LABELS.trade, OFFER_MODE_LABELS.sell];

const LABEL_TO_MODE = Object.fromEntries(Object.entries(OFFER_MODE_LABELS).map(([mode, label]) => [label, mode]));

export function matchesOfferFilter(listing, kinds) {
  if (!Array.isArray(kinds) || kinds.length === 0) return true;
  const mode = offerModeOf(listing);
  return kinds.some((k) => LABEL_TO_MODE[k] === mode);
}
